import React from 'react';
import { useSettings } from '@wix/tpa-settings/react';
import {
  Image,
  ImageResizeOptions,
  Text,
  TextTypography,
} from 'wix-ui-tpa/cssVars';

import { useGetLayoutSize } from '../../../../components-shared/LayoutSizeProvider';
import { displaySettingsParams } from '../../Settings/Tabs/Display/settingsParams';
import { layoutSettingsParams } from '../../Settings/Tabs/Layout/settingsParams';
import { HEADER_PLACEMENT } from '../../constants';
import StatusBadge, { StatusBadgeProps } from '../StatusBadge';
import OrderNow from '../OrderNow';

import { LocationButton } from './LocationButton';
import { getHeaderImageSrc } from './headerImageService';
import { classes, st } from './Header.st.css';
import { headerDataHooks } from './constants';

export type HeaderProps = {
  className?: string;
  title: string;
  description: string;
  statusBarData: StatusBadgeProps[];
  locationName?: string;
  showOrderNow: boolean;
  onLocationButtonClick?: () => void;
};

const Header: React.FC<HeaderProps> = ({
  className,
  title,
  description,
  statusBarData,
  locationName,
  showOrderNow,
  onLocationButtonClick,
}) => {
  const { layoutSize } = useGetLayoutSize();
  const settings = useSettings();

  const showHeaderImage = settings.get(displaySettingsParams.showHeaderImage);
  const imagePlacement = settings.get(
    layoutSettingsParams.headerImagePlacement,
  );

  const isImageOn = showHeaderImage && imagePlacement === HEADER_PLACEMENT.ON;

  return (
    <div
      className={st(
        classes.root,
        { [layoutSize]: true, imageOn: isImageOn },
        className,
      )}
      data-hook={headerDataHooks.root()}
    >
      <div className={classes.headerArea} data-hook={headerDataHooks.content()}>
        {showHeaderImage && (
          <div className={classes.headerImageWrapper}>
            <Image
              fluid
              className={classes.headerImage}
              src={getHeaderImageSrc()}
              resize={ImageResizeOptions.cover}
              alt=""
              aria-hidden="true"
            />
          </div>
        )}
        <div className={classes.textContent}>
          {locationName && onLocationButtonClick && (
            <LocationButton
              locationName={locationName}
              onClick={onLocationButtonClick}
            />
          )}
          {title && (
            <Text
              className={classes.title}
              typography={TextTypography.largeTitle}
              tagName="h1"
              data-hook={headerDataHooks.title()}
            >
              {title}
            </Text>
          )}
          {description && (
            <Text
              className={classes.description}
              typography={TextTypography.runningText}
              tagName="p"
              data-hook={headerDataHooks.description()}
            >
              {description}
            </Text>
          )}
        </div>
      </div>
      {showOrderNow ? <OrderNow /> : null}
      {statusBarData.length > 0 && (
        <div
          className={classes.statusBar}
          data-hook={headerDataHooks.statusBar()}
        >
          {statusBarData.map((props, index) => (
            <StatusBadge key={index} {...props} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Header;
